import type { Dispatch, SetStateAction } from 'react';

import { PAGE_TYPE, PageType } from '../../../constants';

import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch';
import { Endpoints } from '@xver/apis';
import { setErrorMessage } from '../../../../../../store/error/error.actions';

interface useResetPasswordProps {
    setPageType: Dispatch<SetStateAction<PageType>>;
    requestUserId: number | undefined;
}

export function useResetPassword({ setPageType, requestUserId }: useResetPasswordProps) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(['notifications', 'validation']);

    const {
        formState: { isSubmitting },
        handleSubmit,
        setValue,
        register,
        watch,
    } = useForm({
        defaultValues: {
            otpCode: '',
            password: '',
            confirmPassword: '',
        },
    });

    const onSubmit = handleSubmit(async (values) => {
        if (!requestUserId) {
            dispatch(setErrorMessage(t('notifications:auth.missingUserId')));
            return;
        }

        if (values.otpCode.length !== 6) {
            dispatch(setErrorMessage(t('validation:otp.mustBe6Digits')));
            return;
        }

        if (values.password !== values.confirmPassword) {
            dispatch(setErrorMessage(t('validation:password.mismatch')));
            return;
        }

        try {
            await Endpoints.v1.Authentication.confirmResetPassword(requestUserId, {
                otpCode: values.otpCode,
                password: values.password,
            });

            notifications.show({
                title: t('notifications:auth.passwordResetSuccess'),
                message: t('notifications:auth.passwordResetSuccessMessage'),
                icon: <IconCheck size={32} color={'#00ff00'} />,
            });

            setPageType(PAGE_TYPE.SIGN_IN);
            return;
        } catch (error) {
            dispatch(setErrorMessage(t('notifications:auth.passwordResetFailedMessage')));
            return;
        }
    });

    return {
        isSubmitting,
        onSubmit,
        setValue,
        register,
        watch,
    };
}
